import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';

import { MY_ACCOUNT_DATE_FORMAT } from 'constants/appConstants';
import {
  CANCELLED,
  COMPLETED,
  DECLINED,
  DECLINED_CANCELLED,
  DELIVERY_MISTAKE,
  PROCESSING,
  RETURNED,
  RETURNING,
  SHIPPED,
  SUBMITTED
} from 'constants/orderStatuses';

import { ButtonSpinner } from 'components/Loader';
import { Question } from 'components/icons';
import Tooltip from 'components/common/Tooltip';
import MartyLink from 'components/common/MartyLink';
import PreviewIcon from 'images/preview-icon.svg';
import css from 'styles/components/account/orderHistoryTable.scss';
import { getItemStatusClassName } from 'helpers/MyAccountRenderUtils';

const ORDER_STATUSES = {
  [SUBMITTED]: { title: 'Submitted', description: 'Thanks for submitting your order!' },
  [DECLINED]: { title: 'Customer Action Required', description: 'Oh no! It looks like there was a problem with your order. Please update your payment information.' },
  [PROCESSING]: { title: 'Processing', description: 'We\'ve received your order! Thanks for shopping with us!' },
  [CANCELLED]: { title: 'Cancelled', description: 'We\'re sorry you changed your mind about ordering some of your items, and we won\'t charge you for what you don\'t want.' },
  [COMPLETED]: { title: 'Completed', description: 'Your order has been processed!' },
  [SHIPPED]: { title: 'Shipped', description: 'Your order has been shipped out and is en route to its final destination!' },
  [RETURNING]: { title: 'Return in Progress', description: 'We\'re sorry you felt the need to return something. Your label is ready, but your return is still in progress. We will let you know as soon as your return has been processed.' },
  [RETURNED]: { title: 'Returned', description: 'We have processed your return and have issued your credit, if applicable.' },
  [DELIVERY_MISTAKE]: { title: 'Delivery Mistake', description: 'Whoops! Looks like there was a mistake with your delivery. We\'ve taken care of this, but please contact us if you have questions.' },
  [DECLINED_CANCELLED]: { title: 'Canceled - Payment Failure', description: 'Unfortunately, we were unable to authorize your original credit card and another form of payment was not provided, so the order was canceled. Please feel free to resubmit an order at any time.' }
};

const getStatus = status => {
  if (ORDER_STATUSES[status]) {
    return ORDER_STATUSES[status];
  } else {
    return {};
  }
};

const getQVLength = len => {
  const mult = Math.min(3, len);
  return `${130 * mult}px`;
};

const makeStatusTooltipContent = status => {
  const { title, description } = getStatus(status);
  return (
    <div>
      <h4>Order Status</h4>
      {title && description ? <div><strong>{title}</strong> {description}</div> : null}
    </div>
  );
};

const makeAllStatusesTooltipContent = () => (
  <div>
    <h4>Order Status</h4>
    {Object.keys(ORDER_STATUSES).map(key => {
      const { title, description } = ORDER_STATUSES[key];
      return (
        <div key={key}>
          <strong>{title}</strong> {description}
        </div>
      );
    })}
  </div>
);

const makeQuickViewTooltipContent = lineItems => (
  <span className={css.quickViewImages} style={{ width:getQVLength(lineItems.length) }}>
    {lineItems.map(({ product }, index) => {
      const { retinaImageUrl, name } = product;
      return <img key={index} src={retinaImageUrl} alt={name}/>;
    })}
  </span>
);

const OrderHistoryTable = ({ orders, isLoading }, { testId }) => (
  <table className={css.orderTable}>
    <caption className={cn(css.loadingTable, { [css.active]: isLoading })}>
      <div className={css.buttonSpinner}><ButtonSpinner/></div>
    </caption>
    <thead>
      <tr>
        <th className={css.orderDate}>Order Date</th>
        <th className={css.orderNumber}>Order Number</th>
        <th className={css.items}>Items</th>
        <th>Total</th>
        <th>Status <Tooltip
          tooltipId="orderHistory"
          wrapperClassName={css.tooltipWrapper}
          contentClassName={css.tooltipContent}
          tooltipClassName={css.tooltip}
          direction="left"
          content={makeAllStatusesTooltipContent()}>
          <Question size={16} className={css.statusIcon}/>
        </Tooltip></th>
      </tr>
    </thead>

    <tbody>
      {orders.length ? orders.map(({ orderId, orderDate, lineItems, pricing: { totalCharged } }) => {
        // Create an array of unique item statuses (faster than using ES6 Set)
        const statuses = lineItems
          .map(item => item.state)
          .filter((elem, pos, arr) => arr.indexOf(elem) === pos);

        return (
          <tr key={orderId} data-test-id={testId('orderHistoryEntry')}>
            <td data-test-id={testId('orderDate')}>{moment(orderDate, MY_ACCOUNT_DATE_FORMAT).format('ll')}</td>
            <td>
              <MartyLink to={`/orders/${orderId}`} data-test-id={testId('orderLink')}>
                <span className={css.srOnly}>Order number </span>{orderId}
              </MartyLink>
            </td>
            <td data-test-id={testId('itemCount')}>
              {lineItems.length}
              <Tooltip
                tooltipId="quickView"
                wrapperClassName={css.tooltipWrapper}
                contentClassName={css.tooltipContent}
                tooltipClassName={css.quickViewTooltip}
                direction="right"
                content={makeQuickViewTooltipContent(lineItems)}>
                <img className={css.quickView} src={PreviewIcon} alt="Quick View"/>
              </Tooltip>
            </td>
            <td data-test-id={testId('totalCharged')}>{totalCharged}</td>
            <td data-test-id={testId('orderStatus')}>
              {statuses.map((status, index) => (
                <span key={index} className={css.orderStatusWrapper}>
                  <Tooltip
                    tooltipId={status}
                    wrapperClassName={css.tooltipWrapper}
                    contentClassName={css.tooltipContent}
                    tooltipClassName={css.tooltip}
                    direction="left"
                    content={makeStatusTooltipContent(status)}>
                    <span className={getItemStatusClassName(status)}>{status}</span>
                  </Tooltip>
                </span>
              ))}
            </td>
          </tr>
        );
      }) : (
        <tr>
          <td colSpan={5}>Your order history is empty.</td>
        </tr>
      )}
    </tbody>
  </table>
);

OrderHistoryTable.contextTypes = {
  testId: PropTypes.func
};

OrderHistoryTable.propTypes = {
  orders: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired
};
export default OrderHistoryTable;
